import { defineStore } from "pinia";
import { supabase } from "@/supabase";
import { DEFAULT_LOCALE, getBrowserLocale, normalizeLocale } from "@/i18n";
import { getEffectiveUserId, isImpersonating } from "@/utils/impersonation";
import { useFacebookPageIdStore } from "@/stores/facebookPageId";
import {
  ensureCurrentUserInfo,
  fetchCurrentUserBootstrap,
  fetchCurrentUserProfile,
} from "@/api/session";
import { fetchSubscriptionStatus } from "@/api/commerce";

export const useCurrentUserStore = defineStore("currentUser", {
  state: () => ({
    profile: null,
    ensuredProfile: null,
    status: "idle",
    error: "",
    loadPromise: null,
    ensurePromise: null,
    bootstrapPromise: null,
    ensureSessionUserId: null,
    bootstrapSessionUserId: null,
    ensuredForSessionUserId: null,
    loadingEffectiveUserId: null,
    loadingIsImpersonating: false,
    loadingBootstrapEffectiveUserId: null,
    loadingBootstrapIsImpersonating: false,
    lastLoadedEffectiveUserId: null,
    isImpersonating: false,
    billingStatus: null,
    billingLoaded: false,
    billingStatusSessionUserId: null,
    billingStatusPromise: null,
    loadingBillingSessionUserId: null,
    subscriptionStatus: null,
    subscriptionLoaded: false,
    entitlements: null,
    subscriptionStatusSessionUserId: null,
    subscriptionStatusPromise: null,
    loadingSubscriptionSessionUserId: null,
    signingOut: false,
  }),
  getters: {
    currentUserId: (state) => state.profile?.user_id || null,
    currentUserInfoId: (state) => state.profile?.user_info_id ?? null,
    displayName: (state) => {
      const value = state.profile?.use_name;
      return typeof value === "string" ? value.trim() : "";
    },
    avatarUrl: (state) => {
      const value = state.profile?.use_avatar_url;
      return typeof value === "string" ? value.trim() : "";
    },
    customLogoUrl: (state) => {
      const value = state.profile?.user_custom_logo;
      return typeof value === "string" ? value.trim() : "";
    },
    customWatermarkUrl: (state) => {
      const value = state.profile?.user_custom_watermark;
      return typeof value === "string" ? value.trim() : "";
    },
    savedLocale: (state) => normalizeLocale(state.profile?.user_default_lenguage),
    locale: (state) =>
      normalizeLocale(state.profile?.user_default_lenguage) || getBrowserLocale() || DEFAULT_LOCALE,
    timezone: (state) => {
      const value = state.profile?.use_timezone;
      return typeof value === "string" ? value : "";
    },
    phoneNumber: (state) => {
      const value = state.profile?.user_phone;
      return typeof value === "string" ? value.trim() : "";
    },
    useRole: (state) => state.profile?.use_role || null,
    isAdmin: (state) => String(state.profile?.use_role || "").trim().toLowerCase() === "admin",
    hasAdminAccess: (state) =>
      !state.isImpersonating
      && String(state.profile?.use_role || "").trim().toLowerCase() === "admin",
    hasCompletedOnboarding: (state) => state.profile?.use_has_onboarded === true,
    isTwitchUser: (state) => state.profile?.twitch_user === true,
    creditsRemaining: (state) => {
      const billingValue = Number(state.billingStatus?.credits_remaining);
      if (Number.isFinite(billingValue)) {
        return billingValue;
      }
      const value = Number(state.profile?.credits_remaining);
      return Number.isFinite(value) ? value : null;
    },
    hasActiveSubscription: (state) => {
      const payload = state.subscriptionStatus;
      if (!payload || typeof payload !== "object" || !payload.subscription_id) return false;
      const localStatus = String(payload.local_status || "").trim().toLowerCase();
      const providerStatus = String(payload.provider_status || "").trim().toLowerCase();
      if (["active", "trialing"].includes(localStatus)) return true;
      return localStatus === "payment_failed" && providerStatus === "active";
    },
    planTier: (state) => state.entitlements?.plan_tier || null,
    firstFreeStreamAvailable: (state) =>
      state.entitlements?.first_free_stream_available === true,
    firstFreeStreamDeadline: (state) => {
      const createdAtRaw = state.profile?.use_created_at;
      if (typeof createdAtRaw !== "string" || createdAtRaw.trim() === "") return null;
      const createdAtMs = Date.parse(createdAtRaw);
      if (!Number.isFinite(createdAtMs)) return null;
      const deadlineMs = createdAtMs + (24 * 60 * 60 * 1000);
      return new Date(deadlineMs).toISOString();
    },
    canConnectSocials: (state) => state.entitlements?.can_connect_socials === true,
    canSchedulePosts: (state) => state.entitlements?.can_schedule_posts === true,
    canEditClips: (state) => state.entitlements?.can_edit_clips === true,
    canUseAiAnalysis: (state) => state.entitlements?.can_use_ai_analysis === true,
    hasWatermark: (state) => state.entitlements?.has_watermark === true,
  },
  actions: {
    applyBillingStatusSnapshot(snapshot) {
      if (!snapshot || typeof snapshot !== "object") {
        return null;
      }

      this.billingStatus = snapshot;
      this.billingLoaded = true;

      const creditsRemaining = Number(snapshot?.credits_remaining);
      if (Number.isFinite(creditsRemaining)) {
        this.profile = {
          ...(this.profile || {}),
          credits_remaining: creditsRemaining,
        };
      }

      return this.billingStatus;
    },
    clearBillingStatus() {
      this.billingStatus = null;
      this.billingLoaded = false;
      this.billingStatusSessionUserId = null;
      this.billingStatusPromise = null;
      this.loadingBillingSessionUserId = null;
    },
    applySubscriptionStatusSnapshot(payload) {
      if (!payload || typeof payload !== "object") {
        this.subscriptionStatus = null;
        this.subscriptionLoaded = true;
        return this.subscriptionStatus;
      }
      if (payload.entitlements && typeof payload.entitlements === "object") {
        this.entitlements = payload.entitlements;
      }
      if (!payload.subscription_id) {
        this.subscriptionStatus = null;
        this.subscriptionLoaded = true;
        return this.subscriptionStatus;
      }
      this.subscriptionStatus = payload;
      this.subscriptionLoaded = true;
      return this.subscriptionStatus;
    },
    applyBootstrapSnapshot(payload, currentSession, { effectiveUserId, isImpersonating: nextIsImpersonating } = {}) {
      const currentUser = payload?.current_user;
      if (!currentUser || typeof currentUser !== "object") {
        throw new Error("Missing current_user in bootstrap response");
      }

      this.profile = currentUser;
      this.ensuredProfile = payload?.ensured_user_info || null;
      this.ensuredForSessionUserId = currentSession.user.id;
      this.lastLoadedEffectiveUserId = currentUser?.user_id || effectiveUserId;
      this.isImpersonating = nextIsImpersonating;
      this.status = "ready";
      this.error = "";

      if (payload?.billing_status && typeof payload.billing_status === "object") {
        this.billingStatusSessionUserId = currentSession.user.id;
        this.applyBillingStatusSnapshot(payload.billing_status);
      }
      if (Object.prototype.hasOwnProperty.call(payload || {}, "subscription_status")) {
        this.subscriptionStatusSessionUserId = currentSession.user.id;
        this.applySubscriptionStatusSnapshot(payload.subscription_status);
      }
      if (payload?.entitlements && typeof payload.entitlements === "object") {
        this.entitlements = payload.entitlements;
      }

      const facebookPageIdStore = useFacebookPageIdStore(this.$pinia);
      facebookPageIdStore.applyBootstrapPageId(payload?.facebook_page_id ?? "", currentSession);

      return currentUser;
    },
    clearSubscriptionStatus() {
      this.subscriptionStatus = null;
      this.subscriptionLoaded = false;
      this.entitlements = null;
      this.subscriptionStatusSessionUserId = null;
      this.subscriptionStatusPromise = null;
      this.loadingSubscriptionSessionUserId = null;
    },
    async resolveSession(session = null) {
      if (session) {
        return session;
      }
      const {
        data: { session: currentSession },
        error,
      } = await supabase.auth.getSession();
      if (error) {
        throw error;
      }
      return currentSession;
    },
    async ensureUserInfo(session = null) {
      const currentSession = await this.resolveSession(session);
      if (!currentSession?.access_token || !currentSession?.user?.id) {
        return null;
      }

      const sessionUserId = currentSession.user.id;
      if (this.ensuredForSessionUserId === sessionUserId && this.ensuredProfile) {
        return this.ensuredProfile;
      }
      if (this.ensurePromise && this.ensureSessionUserId === sessionUserId) {
        return this.ensurePromise;
      }

      this.ensureSessionUserId = sessionUserId;
      this.ensurePromise = (async () => {
        const payload = await ensureCurrentUserInfo(currentSession);
        this.ensuredForSessionUserId = sessionUserId;
        this.ensuredProfile = payload;
        return payload;
      })().finally(() => {
        this.ensurePromise = null;
        this.ensureSessionUserId = null;
      });

      return this.ensurePromise;
    },
    async fetchMe(session = null, { force = false } = {}) {
      const currentSession = await this.resolveSession(session);
      if (!currentSession?.user?.id) {
        this.clear();
        return null;
      }

      const effectiveUserId = getEffectiveUserId(currentSession.user.id);
      const nextIsImpersonating = isImpersonating();
      const shouldResetCommerceCache = this.lastLoadedEffectiveUserId !== null
        && (
          this.lastLoadedEffectiveUserId !== effectiveUserId
          || this.isImpersonating !== nextIsImpersonating
        );
      if (shouldResetCommerceCache) {
        this.clearBillingStatus();
        this.clearSubscriptionStatus();
      }
      if (
        !force
        && this.profile
        && this.lastLoadedEffectiveUserId === effectiveUserId
        && this.isImpersonating === nextIsImpersonating
      ) {
        return this.profile;
      }
      if (
        this.loadPromise
        && this.loadingEffectiveUserId === effectiveUserId
        && this.loadingIsImpersonating === nextIsImpersonating
      ) {
        return this.loadPromise;
      }

      this.status = "loading";
      this.error = "";
      this.isImpersonating = nextIsImpersonating;
      this.loadingEffectiveUserId = effectiveUserId;
      this.loadingIsImpersonating = nextIsImpersonating;

      this.loadPromise = (async () => {
        const payload = await fetchCurrentUserProfile(currentSession);
        this.profile = payload;
        this.lastLoadedEffectiveUserId = payload?.user_id || effectiveUserId;
        this.isImpersonating = nextIsImpersonating;
        this.status = "ready";
        this.error = "";
        return payload;
      })().catch((error) => {
        this.profile = null;
        this.status = "error";
        this.error = error?.message || String(error);
        throw error;
      }).finally(() => {
        this.loadPromise = null;
        this.loadingEffectiveUserId = null;
        this.loadingIsImpersonating = false;
      });

      return this.loadPromise;
    },
    async syncCommerceStatus(session = null, { force = false } = {}) {
      const currentSession = await this.resolveSession(session);
      if (!currentSession?.access_token || !currentSession?.user?.id) {
        return null;
      }

      const sessionUserId = currentSession.user.id;
      if (
        !force
        && this.subscriptionLoaded
        && this.billingLoaded
        && this.subscriptionStatusSessionUserId === sessionUserId
        && this.billingStatusSessionUserId === sessionUserId
      ) {
        return {
          billingStatus: this.billingStatus,
          subscriptionStatus: this.subscriptionStatus,
        };
      }
      if (
        this.subscriptionStatusPromise
        && this.loadingSubscriptionSessionUserId === sessionUserId
      ) {
        return this.subscriptionStatusPromise;
      }

      this.loadingSubscriptionSessionUserId = sessionUserId;
      this.loadingBillingSessionUserId = sessionUserId;
      this.subscriptionStatusPromise = (async () => {
        const payload = await fetchSubscriptionStatus(currentSession);
        if (!payload?.billing_status || typeof payload.billing_status !== "object") {
          throw new Error("Missing billing_status in subscription status response");
        }

        this.subscriptionStatusSessionUserId = sessionUserId;
        this.billingStatusSessionUserId = sessionUserId;
        this.applyBillingStatusSnapshot(payload.billing_status);
        this.applySubscriptionStatusSnapshot(payload);
        return {
          billingStatus: this.billingStatus,
          subscriptionStatus: this.subscriptionStatus,
        };
      })().finally(() => {
        this.subscriptionStatusPromise = null;
        this.loadingSubscriptionSessionUserId = null;
        this.billingStatusPromise = null;
        this.loadingBillingSessionUserId = null;
      });
      this.billingStatusPromise = this.subscriptionStatusPromise;

      return this.subscriptionStatusPromise;
    },
    async syncCreditsFromBilling(session = null, { force = false } = {}) {
      const commerceSnapshot = await this.syncCommerceStatus(session, { force });
      return commerceSnapshot?.billingStatus || null;
    },
    async syncSubscriptionStatus(session = null, { force = false } = {}) {
      const commerceSnapshot = await this.syncCommerceStatus(session, { force });
      return commerceSnapshot?.subscriptionStatus || null;
    },
    async bootstrap(session = null, { force = false } = {}) {
      if (this.signingOut) {
        return null;
      }
      const currentSession = await this.resolveSession(session);
      if (!currentSession?.user?.id) {
        this.clear();
        return null;
      }

      const sessionUserId = currentSession.user.id;
      const effectiveUserId = getEffectiveUserId(sessionUserId);
      const nextIsImpersonating = isImpersonating();
      const facebookPageIdStore = useFacebookPageIdStore(this.$pinia);
      const shouldResetCommerceCache = this.lastLoadedEffectiveUserId !== null
        && (
          this.lastLoadedEffectiveUserId !== effectiveUserId
          || this.isImpersonating !== nextIsImpersonating
        );
      if (shouldResetCommerceCache) {
        this.clearBillingStatus();
        this.clearSubscriptionStatus();
      }

      const hasFacebookBootstrapSnapshot =
        facebookPageIdStore.lastLoadedEffectiveUserId === effectiveUserId
        && facebookPageIdStore.isImpersonating === nextIsImpersonating
        && (facebookPageIdStore.loaded || facebookPageIdStore.status === "error");

      if (
        !force
        && this.profile
        && this.lastLoadedEffectiveUserId === effectiveUserId
        && this.isImpersonating === nextIsImpersonating
        && hasFacebookBootstrapSnapshot
      ) {
        return this.profile;
      }
      if (
        this.bootstrapPromise
        && this.bootstrapSessionUserId === sessionUserId
        && this.loadingBootstrapEffectiveUserId === effectiveUserId
        && this.loadingBootstrapIsImpersonating === nextIsImpersonating
      ) {
        return this.bootstrapPromise;
      }

      this.status = "loading";
      this.error = "";
      this.isImpersonating = nextIsImpersonating;
      this.bootstrapSessionUserId = sessionUserId;
      this.loadingBootstrapEffectiveUserId = effectiveUserId;
      this.loadingBootstrapIsImpersonating = nextIsImpersonating;

      this.bootstrapPromise = (async () => {
        const payload = await fetchCurrentUserBootstrap(currentSession);
        return this.applyBootstrapSnapshot(payload, currentSession, {
          effectiveUserId,
          isImpersonating: nextIsImpersonating,
        });
      })().catch(async (error) => {
        this.profile = null;
        this.ensuredProfile = null;
        this.ensuredForSessionUserId = null;
        this.status = "error";
        this.error = error?.message || String(error);
        if (error?.status === 401) {
          this.signingOut = true;
          supabase.auth.signOut().catch(() => {}).finally(() => {
            this.signingOut = false;
          });
        }
        throw error;
      }).finally(() => {
        this.bootstrapPromise = null;
        this.bootstrapSessionUserId = null;
        this.loadingBootstrapEffectiveUserId = null;
        this.loadingBootstrapIsImpersonating = false;
      });

      return this.bootstrapPromise;
    },
    async ensureSessionReady(
      session = null,
      { force = false, syncCommerce = false, forceCommerce = false } = {}
    ) {
      const profile = await this.bootstrap(session, { force });
      if (syncCommerce) {
        await this.syncCommerceStatus(session, { force: forceCommerce });
      }
      return profile;
    },
    async refresh(session = null, { syncCommerce = false, forceCommerce = false } = {}) {
      return this.ensureSessionReady(session, { force: true, syncCommerce, forceCommerce });
    },
    clear() {
      this.profile = null;
      this.ensuredProfile = null;
      this.status = "idle";
      this.error = "";
      this.loadPromise = null;
      this.ensurePromise = null;
      this.bootstrapPromise = null;
      this.ensureSessionUserId = null;
      this.bootstrapSessionUserId = null;
      this.ensuredForSessionUserId = null;
      this.loadingEffectiveUserId = null;
      this.loadingIsImpersonating = false;
      this.loadingBootstrapEffectiveUserId = null;
      this.loadingBootstrapIsImpersonating = false;
      this.lastLoadedEffectiveUserId = null;
      this.isImpersonating = false;
      this.signingOut = false;
      this.clearBillingStatus();
      this.clearSubscriptionStatus();
    },
  },
});
